import { WCAG_AA_TEXT, WCAG_NON_TEXT, blendHex, contrastRatio, ensureContrast, isHexColor, } from './color-contrast.utils';
export const BRAND_THEME_CONTRAST_PAIRS = [
    { fg: 'text', bg: 'background', label: 'Text / background' },
    { fg: 'mutedText', bg: 'background', label: 'Muted / background' },
    { fg: 'accent', bg: 'background', label: 'Accent / background' },
    { fg: 'text', bg: 'surface', label: 'Text / surface' },
    { fg: 'mutedText', bg: 'surface', label: 'Muted / surface' },
    { fg: 'accent', bg: 'surface', label: 'Accent / surface' },
    { fg: 'onPrimary', bg: 'primary', label: 'On primary / primary' },
];
const COLOUR_KEYS = [
    'background',
    'surface',
    'primary',
    'onPrimary',
    'text',
    'mutedText',
    'accent',
];
export function brandThemeReadable(theme) {
    return (COLOUR_KEYS.every(key => isHexColor(theme[key])) &&
        BRAND_THEME_CONTRAST_PAIRS.every(pair => contrastRatio(theme[pair.fg], theme[pair.bg]) >= WCAG_AA_TEXT));
}
export function brandPrimaryStandsOut(theme) {
    return (contrastRatio(theme.primary, theme.background) >= WCAG_NON_TEXT &&
        contrastRatio(theme.primary, theme.surface) >= WCAG_NON_TEXT);
}
const RISK_RED = '#DC2626';
const WARNING_AMBER = '#F59E0B';
const WARNING_AMBER_SURFACE = '#FFFBEB';
const WARNING_AMBER_TEXT = '#B45309';
const STATUS_GREEN = '#15803D';
const STATUS_GREEN_ON_PHOTO = '#DCFCE7';
const PHOTO_VEIL = '#1F1F1F';
export function resolveBrandPalette(theme) {
    const { mode, background, surface, primary, onPrimary, text, mutedText, accent } = theme;
    const chip = blendHex(text, background, 0.07);
    const accentSoft = blendHex(accent, surface, 0.15);
    const accentWash = blendHex(accentSoft, background, 0.33);
    const inverseSurface = blendHex(text, background, 0.94);
    const negative = ensureContrast(RISK_RED, surface, WCAG_NON_TEXT);
    const successSurface = blendHex(STATUS_GREEN, surface, 0.16);
    const primaryOutline = brandPrimaryStandsOut(theme) ? undefined : accent;
    return {
        ...theme,
        hairline: blendHex(text, surface, 0.06),
        chip,
        onChip: ensureContrast(mutedText, chip, WCAG_AA_TEXT),
        track: blendHex(text, surface, 0.1),
        dot: blendHex(text, background, 0.35),
        accentSoft,
        onAccentSoft: ensureContrast(text, accentSoft, WCAG_AA_TEXT),
        accentOnSoft: ensureContrast(accent, accentSoft, WCAG_AA_TEXT),
        accentWash,
        onAccentWash: ensureContrast(text, accentWash, WCAG_AA_TEXT),
        inverseSurface,
        onInverseSurface: ensureContrast(background, inverseSurface, WCAG_AA_TEXT),
        onPrimaryMuted: blendHex(onPrimary, primary, 0.8),
        primaryMark: contrastRatio(primary, background) >= WCAG_NON_TEXT ? primary : accent,
        ...(primaryOutline ? { primaryOutline } : {}),
        onAccent: ensureContrast(background, accent, WCAG_AA_TEXT),
        accentBorder: blendHex(accent, surface, 0.3),
        chipBorder: blendHex(text, background, 0.12),
        iconPlate: mode === 'light'
            ? blendHex(accent, background, 0.14)
            : contrastRatio(surface, background) >= 1.1
                ? surface
                : blendHex(text, background, 0.1),
        primaryTint: blendHex(background, primary, 0.26),
        pageGradientEnd: blendHex(background, text, 0.91),
        accentOnInverse: ensureContrast(accent, inverseSurface, WCAG_AA_TEXT),
        primaryOnPhoto: ensureContrast(primary, PHOTO_VEIL, WCAG_NON_TEXT),
        warningSurface: WARNING_AMBER_SURFACE,
        warningBorder: WARNING_AMBER,
        onWarningSurface: ensureContrast(WARNING_AMBER_TEXT, WARNING_AMBER_SURFACE, WCAG_AA_TEXT),
        danger: ensureContrast(RISK_RED, background, WCAG_AA_TEXT),
        negative,
        negativeDim: blendHex(negative, surface, 0.3),
        positive: ensureContrast(STATUS_GREEN, surface, WCAG_NON_TEXT),
        successSurface,
        onSuccessSurface: ensureContrast(STATUS_GREEN, successSurface, WCAG_AA_TEXT),
        success: ensureContrast(STATUS_GREEN, surface, WCAG_NON_TEXT),
        badgeOnPhoto: STATUS_GREEN_ON_PHOTO,
        onBadgeOnPhoto: ensureContrast(STATUS_GREEN, STATUS_GREEN_ON_PHOTO, WCAG_AA_TEXT),
    };
}
const logoDrawnFor = (brand) => brand.logoMode ?? brand.theme.mode;
export function brandLogoTint(brand, painted) {
    return logoDrawnFor(brand) === painted.mode ? undefined : painted.text;
}
export function brandLogoPlate(brand) {
    const drawnFor = logoDrawnFor(brand);
    if (drawnFor === brand.theme.mode)
        return brand.theme.background;
    return drawnFor === 'light' ? '#FFFFFF' : '#000000';
}
//# sourceMappingURL=brand-palette.utils.js.map